<main class="page-shell dashboard-root">
  <section *ngIf="loading" class="card p-6 text-sm text-[var(--text-secondary)]">Loading dashboard...</section>
  <section *ngIf="error" class="card p-6 text-sm status-error">{{ error }}</section>

  <ng-container *ngIf="!loading && !error && profile as p">
    <div class="grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
      <aside class="space-y-4">
        <article class="card dashboard-card p-5">
          <div class="flex items-center gap-3">
            <div class="avatar-ring">
              <img
                *ngIf="p.user.avatar; else fallbackAvatar"
                [src]="p.user.avatar"
                [alt]="p.user.username"
                class="h-16 w-16 rounded-xl object-cover"
              />
              <ng-template #fallbackAvatar>
                <div class="avatar-fallback">{{ p.user.username.slice(0, 2).toUpperCase() }}</div>
              </ng-template>
            </div>
            <div>
              <h1 class="text-lg font-semibold leading-tight text-[var(--text-primary)]">{{ p.user.username }}</h1>
              <p class="mt-1 text-sm text-[var(--text-secondary)]">{{ p.user.country }}</p>
              <p class="mt-1 text-sm font-medium text-[var(--text-primary)]">Rank {{ formatRank(p.user.rank) }}</p>
            </div>
          </div>
          <a
            routerLink="/profile"
            class="mt-4 inline-flex w-full items-center justify-center rounded-lg border border-emerald-500/50 bg-emerald-500/12 px-3 py-2 text-sm font-semibold text-emerald-500 transition hover:bg-emerald-500/20"
          >
            Edit profile
          </a>
        </article>

        <article class="card dashboard-card p-5">
          <h2 class="text-base font-semibold text-[var(--text-primary)]">Community Stats</h2>
          <ul class="mt-3 space-y-2.5 text-sm">
            <li class="stat-row">
              <span>Profile views</span>
              <strong>{{ formatCount(p.community.profileViews) }}</strong>
            </li>
            <li class="stat-row">
              <span>Solutions posted</span>
              <strong>{{ formatCount(p.community.solutionsPosted) }}</strong>
            </li>
            <li class="stat-row">
              <span>Discussions</span>
              <strong>{{ formatCount(p.community.discussions) }}</strong>
            </li>
            <li class="stat-row">
              <span>Reputation points</span>
              <strong>{{ formatCount(p.community.reputation) }}</strong>
            </li>
          </ul>
        </article>

      </aside>

      <section class="space-y-4">
        <article class="card dashboard-card p-5">
          <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div class="metric-box">
              <p class="metric-label">Contest Rating</p>
              <p class="metric-value">{{ formatCount(p.contest.contestRating) }}</p>
            </div>
            <div class="metric-box">
              <p class="metric-label">Global Ranking</p>
              <p class="metric-value">{{ formatRank(p.contest.globalRanking) }}</p>
            </div>
            <div class="metric-box">
              <p class="metric-label">Contests Attended</p>
              <p class="metric-value">{{ formatCount(p.contest.contestsAttended) }}</p>
            </div>
            <div class="metric-box">
              <p class="metric-label">Top Percentage</p>
              <p class="metric-value">Top {{ p.contest.topPercentage | number: '1.0-2' }}%</p>
            </div>
          </div>

          <div class="mt-5 rounded-xl border border-[var(--border)] bg-[var(--surface-soft)]/70 p-3.5">
            <div class="mb-2 flex items-center justify-between">
              <h3 class="text-sm font-semibold text-[var(--text-primary)]">Contest Rating History</h3>
              <span class="text-xs text-[var(--text-tertiary)]">{{ p.ratingHistory.length }} points</span>
            </div>
            <div class="rating-chart-wrap">
              <canvas #ratingChart></canvas>
            </div>
          </div>
        </article>

        <div class="grid gap-4 2xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <article class="card dashboard-card p-5">
            <h2 class="text-base font-semibold text-[var(--text-primary)]">Solved Problems</h2>
            <div class="mt-4 grid gap-4 md:grid-cols-[9rem_minmax(0,1fr)] md:items-center">
              <div class="solved-ring">
                <span class="text-3xl font-bold">{{ formatCount(p.solved.total) }}</span>
                <span class="mt-1 text-xs uppercase tracking-[0.12em] text-[var(--text-tertiary)]">Solved</span>
              </div>

              <div class="space-y-4">
                <div class="space-y-1.5">
                  <div class="flex items-center justify-between text-sm">
                    <span class="font-medium text-emerald-500">Beginner</span>
                    <span class="text-[var(--text-secondary)]">{{ formatCount(p.solved.beginner) }}</span>
                  </div>
                  <div class="h-2.5 overflow-hidden rounded-full bg-emerald-500/15">
                    <div class="h-full rounded-full bg-emerald-500" [style.width.%]="difficultyPercent(p.solved.beginner)"></div>
                  </div>
                </div>

                <div class="space-y-1.5">
                  <div class="flex items-center justify-between text-sm">
                    <span class="font-medium text-amber-500">Intermediate</span>
                    <span class="text-[var(--text-secondary)]">{{ formatCount(p.solved.intermediate) }}</span>
                  </div>
                  <div class="h-2.5 overflow-hidden rounded-full bg-amber-500/15">
                    <div class="h-full rounded-full bg-amber-500" [style.width.%]="difficultyPercent(p.solved.intermediate)"></div>
                  </div>
                </div>

                <div class="space-y-1.5">
                  <div class="flex items-center justify-between text-sm">
                    <span class="font-medium text-rose-500">Advanced</span>
                    <span class="text-[var(--text-secondary)]">{{ formatCount(p.solved.advanced) }}</span>
                  </div>
                  <div class="h-2.5 overflow-hidden rounded-full bg-rose-500/15">
                    <div class="h-full rounded-full bg-rose-500" [style.width.%]="difficultyPercent(p.solved.advanced)"></div>
                  </div>
                </div>
              </div>
            </div>
          </article>

          <article class="card dashboard-card p-5">
            <div class="flex items-center justify-between">
              <h2 class="text-base font-semibold text-[var(--text-primary)]">Badges</h2>
              <span class="text-sm text-[var(--text-secondary)]">{{ badges.length }}</span>
            </div>
            <div class="mt-4 grid gap-3 sm:grid-cols-2">
              <article *ngFor="let badge of badges; trackBy: trackByBadge" class="badge-item">
                <div class="badge-icon">{{ getBadgeGlyph(badge.icon) }}</div>
                <div>
                  <h3 class="text-sm font-semibold text-[var(--text-primary)]">{{ badge.title }}</h3>
                  <p class="mt-1 text-xs leading-5 text-[var(--text-secondary)]">{{ badge.description }}</p>
                </div>
              </article>
            </div>
          </article>
        </div>

        <article class="card dashboard-card p-5">
          <div class="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 class="text-base font-semibold text-[var(--text-primary)]">Submission Activity</h2>
              <p class="text-sm text-[var(--text-secondary)]">
                {{ formatCount(activityStats.totalSubmissions) }} submissions this year
              </p>
            </div>
            <div class="flex flex-wrap gap-4 text-xs">
              <div class="activity-meta">
                <span class="meta-label">Active days</span>
                <strong>{{ formatCount(activityStats.totalActiveDays) }}</strong>
              </div>
              <div class="activity-meta">
                <span class="meta-label">Current streak</span>
                <strong>{{ formatCount(activityStats.currentStreak) }}</strong>
              </div>
              <div class="activity-meta">
                <span class="meta-label">Max streak</span>
                <strong>{{ formatCount(activityStats.maxStreak) }}</strong>
              </div>
            </div>
          </div>

          <div #heatmapScroller class="mt-4 overflow-x-auto pb-2">
            <div class="heatmap-shell">
              <div class="heatmap-months">
                <span *ngFor="let month of monthLabels; trackBy: trackByMonth" class="heatmap-month" [style.left.px]="month.column * 17">
                  {{ month.label }}
                </span>
              </div>

              <div class="heatmap-grid">
                <div *ngFor="let week of heatmapWeeks; trackBy: trackByWeek" class="heatmap-week">
                  <button
                    type="button"
                    *ngFor="let cell of week; trackBy: trackByCell"
                    [ngClass]="heatmapClass(cell)"
                    [attr.title]="heatmapTooltip(cell)"
                    [disabled]="!cell.inRange"
                    aria-label="submission day"
                  ></button>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3 flex items-center justify-end gap-2 text-xs text-[var(--text-tertiary)]">
            <span>Less</span>
            <span class="heatmap-legend heatmap-l0"></span>
            <span class="heatmap-legend heatmap-l1"></span>
            <span class="heatmap-legend heatmap-l2"></span>
            <span class="heatmap-legend heatmap-l3"></span>
            <span class="heatmap-legend heatmap-l4"></span>
            <span>More</span>
          </div>

          <div class="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <article class="activity-card">
              <span>Total active days</span>
              <strong>{{ formatCount(activityStats.totalActiveDays) }}</strong>
            </article>
            <article class="activity-card">
              <span>Current streak</span>
              <strong>{{ formatCount(activityStats.currentStreak) }} days</strong>
            </article>
            <article class="activity-card">
              <span>Maximum streak</span>
              <strong>{{ formatCount(activityStats.maxStreak) }} days</strong>
            </article>
            <article class="activity-card">
              <span>Total submissions</span>
              <strong>{{ formatCount(activityStats.totalSubmissions) }}</strong>
            </article>
          </div>
        </article>
      </section>
    </div>
  </ng-container>
</main>
